import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  WorkProcessSummary, 
  WorkProcessFormData,
  workProcessService 
} from '@/services/workprocess/workprocessService';

interface WorkProcessState {
  workProcessSummary: WorkProcessSummary | null;
  workProcessSummaryLoading: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: WorkProcessState = {
  workProcessSummary: null,
  workProcessSummaryLoading: false,
  isLoading: false,
  error: null,
};

export const fetchWorkProcessSummary = createAsyncThunk(
  'workProcess/fetchWorkProcessSummary',
  async (poId: string) => {
    const response = await workProcessService.getWorkProcessSummary(poId);
    return response.data;
  }
);

export const createWorkProcess = createAsyncThunk(
  'workProcess/createWorkProcess',
  async ({ level, data }: { level: number; data: WorkProcessFormData }) => {
    const response = await workProcessService.createWorkProcess(level, data);
    return { level, data: response.data };
  }
);

export const updateWorkProcess = createAsyncThunk(
  'workProcess/updateWorkProcess',
  async ({ level, id, data }: { level: number; id: string; data: WorkProcessFormData }) => {
    const response = await workProcessService.updateWorkProcess(level, id, data);
    return { level, data: response.data };
  }
);

export const workProcessSlice = createSlice({
  name: 'workProcess',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearWorkProcessSummary: (state) => {
      state.workProcessSummary = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkProcessSummary.pending, (state) => {
        state.workProcessSummaryLoading = true;
        state.error = null;
      })
      .addCase(fetchWorkProcessSummary.fulfilled, (state, action) => {
        state.workProcessSummaryLoading = false;
        state.workProcessSummary = action.payload;
      })
      .addCase(fetchWorkProcessSummary.rejected, (state, action) => {
        state.workProcessSummaryLoading = false;
        state.error = action.error.message || 'Failed to fetch work process summary';
      })
      .addCase(createWorkProcess.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createWorkProcess.fulfilled, (state) => {
        state.isLoading = false;
        // Optionally update the summary data here
      })
      .addCase(createWorkProcess.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to create work process';
      })
      .addCase(updateWorkProcess.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateWorkProcess.fulfilled, (state) => {
        state.isLoading = false;
        // Optionally update the summary data here
      })
      .addCase(updateWorkProcess.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update work process';
      });
  },
});

export const { clearError, clearWorkProcessSummary } = workProcessSlice.actions;
export default workProcessSlice.reducer;